import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getServiceId } from '@/api/tokens.helper';
import { hasPendingMedia, pollWhileMediaPending } from '@/common/media';

import { deleteGalleryImage, getGalleryImages, getGalleryUploadEvents } from './gallery.api';
import type { GalleryDeleteScope, GalleryScope } from './gallery.dto';

/**
 * One gallery space. `enabled` is how a caller without `InternalGallery` keeps
 * from asking for the vessel's, which the service answers with a 403.
 *
 * Keyed by the vessel as well: both spaces are resolved from the token, so after
 * a vessel switch the same key would otherwise show the last vessel's photos
 * until the refetch lands.
 *
 * A video still being processed keeps it asking: activity tells nobody when the
 * encoder is done with one.
 */
export const useGalleryImagesQuery = (scope: GalleryScope, enabled = true) => {
  const serviceId = getServiceId();
  return useQuery({
    queryKey: ['gallery', serviceId, scope],
    queryFn: () => getGalleryImages(scope),
    enabled: enabled && !!serviceId,
    refetchInterval: pollWhileMediaPending(hasPendingMedia),
  });
};

/**
 * Several rows at once, because one photo can be several rows in the same gallery.
 * Invalidates both spaces, whichever the deletion reached: a photo can sit in each.
 */
export const useDeleteGalleryImageMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, scope }: { ids: string[]; scope: GalleryDeleteScope }) =>
      Promise.all(ids.map((id) => deleteGalleryImage(id, scope))),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['gallery'] }),
  });
};

/** The events a photo may be added to — see `getGalleryUploadEvents`. */
export const useGalleryUploadEventsQuery = (enabled = true) => {
  const serviceId = getServiceId();
  return useQuery({
    queryKey: ['gallery', serviceId, 'upload-events'],
    queryFn: getGalleryUploadEvents,
    enabled: enabled && !!serviceId,
  });
};
